import { FocusOrigin } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty, coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
  Optional,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { CanColor, mixinColor } from '@angular/material/core';
import {
  MTNA_I18N_DEFAULT_OPTIONS,
  MtnaI18nDefaultOptions,
  MtnaI18nLanguageOption,
  MtnaI18nService,
  MtnaI18nString,
  MtnaLanguageIds,
  MtnaLocaleIds,
  mtnaToLanguageId,
} from '@mtna/core-i18n-angular';
import { MtnaInlineEditorAction } from '@mtna/core-angular';
import { BehaviorSubject } from 'rxjs';

const _Base = mixinColor(
  class {
    constructor(public _elementRef: ElementRef) {}
  },
  'primary'
);

@Component({
  selector: 'rds-c-i18n-rich-text-editor',
  templateUrl: 'i18n-wrapper.component.html',
  styleUrls: ['i18n-wrapper.component.scss'],
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['color'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsCI18nRichTextEditorComponent extends _Base implements CanColor, OnDestroy {
  static ngAcceptInputType_alwaysShowOtherLanguages: BooleanInput;
  static ngAcceptInputType_applying: BooleanInput;
  static ngAcceptInputType_defaultLocaleId: typeof MtnaLanguageIds.type | null | undefined;
  static ngAcceptInputType_hideEditorControls: BooleanInput;
  static ngAcceptInputType_languages: MtnaI18nLanguageOption[] | null | undefined;
  static ngAcceptInputType_maxTabs: NumberInput;
  static ngAcceptInputType_readonly: BooleanInput;
  static ngAcceptInputType_value: MtnaI18nString[] | null | undefined;

  /** Whether to always display the secondary language(s) in view mode. Defaults to false. */
  @Input()
  get alwaysShowOtherLanguages(): boolean {
    return this._alwaysShowOtherLanguages;
  }

  set alwaysShowOtherLanguages(alwaysShowOtherLanguages: boolean) {
    this._alwaysShowOtherLanguages = coerceBooleanProperty(alwaysShowOtherLanguages);
  }

  private _alwaysShowOtherLanguages = false;

  /** Whether a change is currently being applied */
  @Input()
  get applying(): boolean {
    return this._applying;
  }

  set applying(applying: boolean) {
    this._applying = coerceBooleanProperty(applying);
  }

  private _applying = false;

  /** Language to display as the primary/default input.
   * @remarks If none provided, defaults to {@link MtnaI18nString} with no langPropName or first {@link MtnaI18nString} */
  @Input()
  get defaultLanguageId(): typeof MtnaLanguageIds.type {
    return this._defaultLanguageId;
  }

  set defaultLanguageId(defaultLocaleId: typeof MtnaLanguageIds.type) {
    this._defaultLanguageId = MtnaLanguageIds.guard(defaultLocaleId) ? defaultLocaleId : 'en';
  }

  private _defaultLanguageId: typeof MtnaLanguageIds.type = 'en';

  @Input()
  get hideEditorControls(): boolean {
    return this._hideEditorControls;
  }

  set hideEditorControls(hideEditorControls: boolean) {
    this._hideEditorControls = coerceBooleanProperty(hideEditorControls);
  }

  private _hideEditorControls = false;

  @Input() langPropName = 'facetId';

  /** Available languages to display. */
  @Input()
  get languages(): MtnaI18nLanguageOption[] {
    return this._languages;
  }

  set languages(languages: MtnaI18nLanguageOption[]) {
    this._languages = languages ?? [];
  }

  private _languages: MtnaI18nLanguageOption[] = [];

  /** The maximum number of tabs to display before converting to a dropdown. Defaults to 3. */
  @Input()
  get maxTabs(): number {
    return this._maxTabs;
  }

  set maxTabs(maxTabs: number) {
    this._maxTabs = coerceNumberProperty(maxTabs);
  }

  private _maxTabs = 3;

  @Input()
  get readonly(): boolean {
    return this._readonly;
  }

  set readonly(readonly: boolean) {
    this._readonly = coerceBooleanProperty(readonly);
  }

  private _readonly = false;

  @Input()
  get value(): MtnaI18nString[] {
    return this._value;
  }

  set value(value: MtnaI18nString[]) {
    this._value = value ?? [];
    this._internalResetValue = value ?? [];
    this._generateValueSubjects(this._value);
  }

  private _value: MtnaI18nString[] = [];
  private _internalResetValue: MtnaI18nString[] = [];
  _internalValueSubjects: { [lang: string]: BehaviorSubject<string> } = {};
  _internalValueChanges?: MtnaI18nString[];

  @Input() valuePropName = 'value';

  @Output() valueChange = new EventEmitter<MtnaI18nString[]>();

  /* Focus of the primary editor. For showing active border. */
  _primaryFocus: FocusOrigin | undefined;
  /* Focus of the secondary editor. For showing active border. */
  _secondaryFocus: FocusOrigin | undefined;
  /* Focus of the container, for activating indicator & keeping secondary open. */
  _focused: FocusOrigin | undefined;

  constructor(
    _elementRef: ElementRef,
    protected i18nService: MtnaI18nService,
    @Inject(LOCALE_ID) protected locale: string,
    @Optional() @Inject(MTNA_I18N_DEFAULT_OPTIONS) _defaults: MtnaI18nDefaultOptions
  ) {
    super(_elementRef);
    const injectedLocale = MtnaLocaleIds.guard(this.locale) ? this.locale : 'en';
    this.defaultLanguageId = mtnaToLanguageId[_defaults?.defaultLocaleId ?? injectedLocale];
    if (_defaults?.languages) {
      this.languages = _defaults.languages;
    }
    if (_defaults?.langPropName) {
      this.langPropName = _defaults.langPropName;
    }
    if (_defaults?.valuePropName) {
      this.valuePropName = _defaults.valuePropName;
    }
  }

  _handlePrimaryFocus(origin: FocusOrigin) {
    this._primaryFocus = origin;
  }

  _handleSecondaryFocus(origin: FocusOrigin) {
    this._secondaryFocus = origin;
  }

  handleEditorControlAction(action: MtnaInlineEditorAction) {
    if (action === 'CANCEL') {
      this.value = this._internalResetValue.slice();
    } else if (this._internalValueChanges) {
      this.valueChange.emit(this._internalValueChanges);
    }
    this._internalValueChanges = undefined;
  }

  handleValueChange(lang: typeof MtnaLanguageIds.type, value: string) {
    const currentValues = this._internalValueChanges ?? this.value;
    this._internalValueChanges = this.i18nService.setI18nValue(value, lang, currentValues, true, this.langPropName, this.valuePropName);
    if (this.hideEditorControls) {
      this.handleEditorControlAction('APPLY');
    }
  }

  ngOnDestroy() {
    // Close all observables
    for (const key in this._internalValueSubjects) {
      if (Object.prototype.hasOwnProperty.call(this._internalValueSubjects, key)) {
        this._internalValueSubjects[key].complete();
      }
    }
  }

  protected _generateValueSubjects(values: MtnaI18nString[]) {
    if (!values || values.length == 0) {
      // reset the subject values for all the languages
      Object.entries(this._internalValueSubjects).forEach(([lang, subject]) => {
        subject = new BehaviorSubject<string>('');
        this._internalValueSubjects[lang] = subject;
      });
    } else {
      for (const value of values) {
        if (value[this.langPropName]) {
          let subject = this._internalValueSubjects[value[this.langPropName]];
          if (!subject) {
            subject = new BehaviorSubject<string>('');
            this._internalValueSubjects[value[this.langPropName]] = subject;
          }
          subject.next(value[this.valuePropName]);
        }
      }
    }
  }
}
